import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Location } from '@angular/common';
import { KeyVaultDTO, MapKeysDTO, RulesDTO } from '@DTOs';
import { FeatureToggle } from '@Enums';
import { Account, Client } from '@Models';
import { FeatureManagerService } from '@Services';
import { SharedModule } from '@Shared/shared.module';
import { forkJoin } from 'rxjs';
import { GeneralSettingsComponent } from './general/general-settings.component';
import { CaseTransferSettingsComponent } from './case-transfer/case-transfer.component';
import { DocumentSigningComponent } from './document-signing/document-signing.component';
import { PurlSettingsComponent } from './purl-settings/purl-settings.component';
import { CaseSummarySidebarComponent } from './case-summary-sidebar';
import { WorkbenchSettingsComponent } from './workbench-settings';
import { InterviewSettingsComponent } from './interview-settings/interview-settings.component';
import { MyCasesComponent } from '@App/my-cases/my-cases.component';
import { ApiMappingsComponent } from './api-mappings/api-mappings.component';
import { AccountSignersComponent } from './account-signers/account-signers.component';
import { MyCasesSettingsComponent } from './my-cases/my-cases-settings.component';
import WorkbenchV2SettingsComponent from './workbench/workbenchv2-settings.component';
import { AccountConfigStateService } from '@Services/account-config-state.service';
import { AdvancedSettingsComponent } from './advanced/advanced-settings.component';
import { AppService } from '@App';

export enum SettingsMenuItemCode {
    GeneralSettings = 'GeneralSettings',
    CaseTransferSettings = 'CaseTransferSettings',
    DocumentSigning = 'DocumentSigning',
    MyCases = 'MyCases',
    ApiMappings = 'ApiMappings',
    InterviewSettings = 'InterviewSettings',
    PurlSettings = 'PurlSettings',
    CaseSummarySidebar = 'CaseSummarySidebar',
    WorkbenchSettings = 'WorkbenchSettings',
    AdvancedSettings = 'AdvancedSettings'
}

export type SettingsMenuItem = {
    code: SettingsMenuItemCode;
    name: string;
    hide: boolean;
}


@Component({
    selector: 'account-settings-v2',
    host: { 'class': 'd-flex flex-row flex-fill' },
    templateUrl: './account-settings-v2.component.html',
    styleUrls: ['./account-settings-v2.component.scss'],
    imports: [SharedModule, InterviewSettingsComponent, MyCasesSettingsComponent, 
        ApiMappingsComponent, GeneralSettingsComponent, CaseTransferSettingsComponent, 
        DocumentSigningComponent, PurlSettingsComponent, CaseSummarySidebarComponent, WorkbenchSettingsComponent, WorkbenchV2SettingsComponent, AdvancedSettingsComponent],
})
export class AccountSettingsV2Component implements OnInit {
    @Input() client: Client;
    @Input() account: Account;
    @Input() rules: RulesDTO[];
    @Input() mapKeys: MapKeysDTO[];
    @Input() secretKeys: KeyVaultDTO[];
    @Input() targetSettingType: string; // For deep linking
    @Input() targetSubTab: string; // For deep linking workbench sub-tabs
    @Output() getSecretKeys: EventEmitter<void>;

    // labels: any[] = []; // TODO: is this being used?
    ftpSettingsForm: UntypedFormGroup;
    generalSettingsForm: UntypedFormGroup;
    showForm = false;
    showContent = false;

    useWorkbenchV2Enabled = false;

    readonly SettingsMenuItemCode = SettingsMenuItemCode;
    protected settings: SettingsMenuItem[] = [
        { code: SettingsMenuItemCode.GeneralSettings, name: 'General', hide: false },
        { code: SettingsMenuItemCode.CaseTransferSettings, name: 'Case Transfer', hide: false },
        { code: SettingsMenuItemCode.DocumentSigning, name: 'Document Signing', hide: false },
        { code: SettingsMenuItemCode.MyCases, name: 'My Cases', hide: false },
        { code: SettingsMenuItemCode.ApiMappings, name: 'Data Import/Export', hide: false },
        { code: SettingsMenuItemCode.InterviewSettings, name: 'Application', hide: false },
        { code: SettingsMenuItemCode.PurlSettings, name: 'Personal URL', hide: false },
        { code: SettingsMenuItemCode.CaseSummarySidebar, name: 'Case Summary Sidebar', hide: true },
        { code: SettingsMenuItemCode.WorkbenchSettings, name: 'Workbench', hide: true },
        { code: SettingsMenuItemCode.AdvancedSettings, name: 'Advanced', hide: true },
    ];

    protected hasSettings = true;
    protected selectedSetting = this.settings.find(x => x.code === SettingsMenuItemCode.GeneralSettings);
    enableDeepLinking: boolean;

    constructor(
        public featureManagerService: FeatureManagerService,
        private readonly stateService: AccountConfigStateService,
        private readonly location: Location,
        public appService: AppService
    ) {
        this.getSecretKeys = new EventEmitter<void>();
    }

    //#region Helpers

    private UpdateHasSettings() {
        this.hasSettings = Object.values(this.settings).filter(x => !x.hide).length > 0;
    }

    //#endregion
    //#region Subscriptions


    subscribeFeatureFlags() {
        forkJoin([
            this.featureManagerService.isEnabled(FeatureToggle.ConfigAccountSettingsWorkbenchConfiguration),
            this.featureManagerService.isEnabled(FeatureToggle.UWWorkbenchHiddenNotesAccountSetting, this.account.clientId),
            this.featureManagerService.isEnabled(FeatureToggle.UWWorkbenchEditableNotesAccountSetting, this.account.clientId),
            this.featureManagerService.isEnabled(FeatureToggle.ConfigSettingsUseWorkbenchV2, this.account.clientId),
        ]).subscribe(([workbenchEnabled, hiddenNotesEnabled, editableNotesEnabled, useWorkbenchV2Enabled]) => {
            if (workbenchEnabled) this.settings.find(x => x.code === SettingsMenuItemCode.CaseSummarySidebar).hide = false;
            if (hiddenNotesEnabled) this.settings.find(x => x.code === SettingsMenuItemCode.WorkbenchSettings).hide = false;
            if (editableNotesEnabled) this.settings.find(x => x.code === SettingsMenuItemCode.WorkbenchSettings).hide = false;

            const hasAdvancedAccess = this.appService.hasViewPermission('Config Settings: Advanced');
            if (hasAdvancedAccess) this.settings.find(x => x.code === SettingsMenuItemCode.AdvancedSettings).hide = false;
            if (useWorkbenchV2Enabled) this.useWorkbenchV2Enabled = true;
            
            this.UpdateHasSettings();
            
            // Handle deep linking after feature flags are loaded
            this.handleDeepLinking();
        });
    }

    //#endregion
    //#region Lifecycle

    async ngOnInit() {
        this.getSecretKeys.emit();
        this.enableDeepLinking = this.featureManagerService.getByName(FeatureToggle.GlobalRoutingDeepLinking).enabled;

        this.subscribeFeatureFlags();
    }
    
    private handleDeepLinking() {
        if (this.targetSettingType) {
            // Map route parameter to setting code
            const settingMap = {
                'general': SettingsMenuItemCode.GeneralSettings,
                'case-transfer': SettingsMenuItemCode.CaseTransferSettings,
                'document-signing': SettingsMenuItemCode.DocumentSigning,
                'my-cases': SettingsMenuItemCode.MyCases,
                'api-mappings': SettingsMenuItemCode.ApiMappings,
                'interview': SettingsMenuItemCode.InterviewSettings,
                'purl': SettingsMenuItemCode.PurlSettings,
                'case-summary-sidebar': SettingsMenuItemCode.CaseSummarySidebar,
                'workbench': SettingsMenuItemCode.WorkbenchSettings
            };
            
            const targetSetting = this.settings.find(s => s.code === settingMap[this.targetSettingType]);
            if (targetSetting) {
                // If the setting is currently hidden, try to enable it based on the request
                if (targetSetting.hide && this.targetSettingType === 'workbench') {
                    // Force enable workbench for deep linking
                    targetSetting.hide = false;
                }
                
                if (targetSetting.hide) {
                    console.warn('Target setting not accessible:', this.targetSettingType);
                    // Fall back to first available setting
                    this.selectedSetting = this.settings.find(s => !s.hide) || this.settings[0];
                } else {
                    this.selectedSetting = targetSetting;
                }
            } else {
                console.warn('Target setting not found:', this.targetSettingType);
                // Fall back to first available setting
                this.selectedSetting = this.settings.find(s => !s.hide) || this.settings[0];
            }
        } else {
            // Restore previous state if not deep linking
            this.restoreSettingsState();
        }
    }

    //#endregion
    //#region Handlers

    setActiveSetting(setting: SettingsMenuItem) {
        this.selectedSetting = setting;
        
        // Update URL to reflect current setting
        //this.updateUrlForSetting(setting);
        
        // Save the selected setting to state
        if (this.account && this.client) {
            const settingTypeMap = {
                [SettingsMenuItemCode.GeneralSettings]: 'general',
                [SettingsMenuItemCode.CaseTransferSettings]: 'case-transfer',
                [SettingsMenuItemCode.DocumentSigning]: 'document-signing',
                [SettingsMenuItemCode.MyCases]: 'my-cases',
                [SettingsMenuItemCode.ApiMappings]: 'api-mappings',
                [SettingsMenuItemCode.InterviewSettings]: 'interview',
                [SettingsMenuItemCode.PurlSettings]: 'purl',
                [SettingsMenuItemCode.CaseSummarySidebar]: 'case-summary-sidebar',
                [SettingsMenuItemCode.WorkbenchSettings]: 'workbench'
            };
            
            this.stateService.updateState(this.client.code, this.account.code, this.account.versionNumber, {
                selectedSetting: settingTypeMap[setting.code]
            });
        }
    }
    
    private restoreSettingsState() {
        // Only restore if not deep linking
        if (!this.targetSettingType && this.account && this.client) {
            const savedState = this.stateService.getState(this.client.code, this.account.code, this.account.versionNumber);
            if (savedState?.selectedSetting) {
                const settingMap = {
                    'general': SettingsMenuItemCode.GeneralSettings,
                    'case-transfer': SettingsMenuItemCode.CaseTransferSettings,
                    'document-signing': SettingsMenuItemCode.DocumentSigning,
                    'my-cases': SettingsMenuItemCode.MyCases,
                    'api-mappings': SettingsMenuItemCode.ApiMappings,
                    'interview': SettingsMenuItemCode.InterviewSettings,
                    'purl': SettingsMenuItemCode.PurlSettings,
                    'case-summary-sidebar': SettingsMenuItemCode.CaseSummarySidebar,
                    'workbench': SettingsMenuItemCode.WorkbenchSettings
                };
                
                const targetSetting = this.settings.find(s => s.code === settingMap[savedState.selectedSetting]);
                if (targetSetting && !targetSetting.hide) {
                    this.selectedSetting = targetSetting;
                }
            }
        }
    }
    
    private updateUrlForSetting(setting: SettingsMenuItem) {
        if (!this.enableDeepLinking || (!this.account || !this.client)) return;
        
        const settingTypeMap = {
            [SettingsMenuItemCode.GeneralSettings]: 'general',
            [SettingsMenuItemCode.CaseTransferSettings]: 'case-transfer',
            [SettingsMenuItemCode.DocumentSigning]: 'document-signing',
            [SettingsMenuItemCode.MyCases]: 'my-cases',
            [SettingsMenuItemCode.ApiMappings]: 'api-mappings',
            [SettingsMenuItemCode.InterviewSettings]: 'interview',
            [SettingsMenuItemCode.PurlSettings]: 'purl',
            [SettingsMenuItemCode.CaseSummarySidebar]: 'case-summary-sidebar',
            [SettingsMenuItemCode.WorkbenchSettings]: 'workbench'
        };
        
        const settingType = settingTypeMap[setting.code];
        if (settingType) {
            // Update URL without reloading the component
            const newUrl = `/client/${this.client.code}/account/${this.account.code}/${this.account.versionNumber}/settings/${settingType}`;
            this.location.replaceState(newUrl);
        }
    }

    //#endregion
}
